import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { leaderboardAPI } from '../services/api';
import { Trophy, Medal, Users, Target } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Leaderboard = () => {
  const { user } = useAuth();
  const [leaderboardType, setLeaderboardType] = useState('global'); // 'global' or 'server'
  const [gameType, setGameType] = useState(null); // null for overall, or specific game
  const [limit, setLimit] = useState(10);

  // Mock server ID for now - in real app, this would come from user's connected servers
  const serverId = 'demo-server-123';
  const serverName = 'Demo Server';

  const { data: leaderboardResponse, isLoading } = useQuery(
    ['leaderboard', leaderboardType, gameType, limit, serverId],
    async () => {
      if (leaderboardType === 'server') {
        return leaderboardAPI.getServerLeaderboard(serverId, { gameType, limit });
      } else {
        return leaderboardAPI.getGlobalLeaderboard({ gameType, limit });
      }
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
    }
  );

  // Extract leaderboard data from response
  const leaderboard = leaderboardResponse?.data?.success ? leaderboardResponse.data.data : leaderboardResponse?.data;

  const getMedalIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Medal className="h-6 w-6 text-yellow-400" />;
      case 2:
        return <Medal className="h-6 w-6 text-gray-300" />;
      case 3:
        return <Medal className="h-6 w-6 text-amber-600" />;
      default:
        return <span className="text-discord-light font-bold">#{rank}</span>;
    }
  };


  if (isLoading) {
    return <LoadingSpinner size="xl" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-discord-lightest mb-2">
              🏆 Leaderboard
            </h1>
            <p className="text-discord-light">
              See who's dominating the gaming scene! Rankings update in real-time.
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <Trophy className="h-8 w-8 text-discord-yellow" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <label className="text-discord-light font-medium">Leaderboard:</label>
            <select
              value={leaderboardType}
              onChange={(e) => setLeaderboardType(e.target.value)}
              className="input w-auto"
            >
              <option value="global">🌍 Global</option>
              <option value="server">🏠 Server</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="text-discord-light font-medium">Game:</label>
            <select
              value={gameType || 'overall'}
              onChange={(e) => setGameType(e.target.value === 'overall' ? null : e.target.value)}
              className="input w-auto"
            >
              <option value="overall">All Games</option>
              <option value="tic-tac-toe">❌⭕ Tic-Tac-Toe</option>
              <option value="number-guess">🔢 Number Guess</option>
              <option value="rock-paper-scissors">🪨📄✂️ Rock Paper Scissors</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="text-discord-light font-medium">Show:</label>
            <select
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
              className="input w-auto"
            >
              <option value={10}>Top 10</option>
              <option value={20}>Top 20</option>
              <option value={50}>Top 50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="card">
        <h2 className="text-xl font-bold text-discord-lightest mb-6">
          {leaderboardType === 'global' && gameType === null && '🌍 Global Leaderboard'}
          {leaderboardType === 'server' && gameType === null && `🏠 ${serverName} Leaderboard`}
          {leaderboardType === 'global' && gameType && `🌍 Global ${gameType.charAt(0).toUpperCase() + gameType.slice(1)} Leaderboard`}
          {leaderboardType === 'server' && gameType && `🏠 ${serverName} - ${gameType.charAt(0).toUpperCase() + gameType.slice(1)} Leaderboard`}
        </h2>

        {leaderboard?.leaderboard && leaderboard.leaderboard.length > 0 ? (
          <div className="space-y-3">
            {leaderboard.leaderboard.map((player, index) => (
              <div
                key={player.username}
                className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                  index < 3
                    ? 'bg-gradient-to-r from-discord-blurple/10 to-discord-fuchsia/10 border border-discord-blurple/20'
                    : 'bg-discord-dark hover:bg-discord-dark/80'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center w-8">
                    {getMedalIcon(player.rank)}
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-full bg-discord-blurple flex items-center justify-center">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-discord-lightest font-medium">
                          {player.username}
                        </span>
                      </div>
                      <span className="text-sm text-discord-light">
                        Player ID: {player._id || player.playerId}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  <div className="text-center">
                    <div className="text-sm text-discord-light">Games</div>
                    <div className="text-lg font-bold text-discord-lightest">
                      {player.totalGames}
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-sm text-discord-light">Win Rate</div>
                    <div className="text-lg font-bold text-discord-lightest">
                      {Math.round(player.winRate || 0)}%
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-sm text-discord-light">Points</div>
                    <div className="text-lg font-bold text-discord-lightest">
                      {player.totalPoints || 0}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Trophy className="h-16 w-16 text-discord-light mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-discord-lightest mb-2">
              {leaderboard?.leaderboard && leaderboard.leaderboard.length === 0 
                ? 'No players yet!' 
                : 'Loading leaderboard...'}
            </h3>
            <p className="text-discord-light">
              {leaderboard?.leaderboard && leaderboard.leaderboard.length === 0
                ? 'Be the first to play and get on the leaderboard!'
                : 'Please wait while we fetch the latest data...'}
            </p>
          </div>
        )}
      </div>

      {/* Stats Summary */}
      {leaderboard?.leaderboard && leaderboard.leaderboard.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card text-center">
            <Users className="h-8 w-8 text-discord-blurple mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-discord-lightest">Total Players</h3>
            <p className="text-2xl font-bold text-discord-lightest">
              {leaderboard.leaderboard.length}
            </p>
          </div>
          
          <div className="card text-center">
            <Target className="h-8 w-8 text-discord-green mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-discord-lightest">Average Win Rate</h3>
            <p className="text-2xl font-bold text-discord-lightest">
              {Math.round(
                leaderboard.leaderboard.reduce((sum, player) => sum + (player.winRate || 0), 0) /
                leaderboard.leaderboard.length
              )}%
            </p>
          </div>
          
          <div className="card text-center">
            <Trophy className="h-8 w-8 text-discord-yellow mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-discord-lightest">Total Games</h3>
            <p className="text-2xl font-bold text-discord-lightest">
              {leaderboard.leaderboard.reduce((sum, player) => sum + player.totalGames, 0)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
